/**
 * Widget Editor Core Mixin
 * 
 * Provides shared functionality for all widget editor components.
 * Handles common concerns - auto-save, preview management, publishing, archiving, and integration features.
 * 
 * Components using this mixin must implement certain abstract methods to specify widget-specific behavior.
 * See method documentation below for required overrides.
 */

import { createParentMessageHandler, WIDGET_TO_PARENT } from '@/js/widget/widget-messaging.js';

export default {
  data() {
    // in each editor component, override anything within data() to add widget-specific initial state
    return {
      // form state
      form: null,
      saving: false,
      save_debounce_handle: null,
      suppress_autosave: true,

      // preview state
      preview_nonce: 0,
      is_preview_loading: false,
      preview_mode: 'tablet-landscape', // 'mobile', 'tablet', 'tablet-landscape', 'desktop'
      current_page: 'form', // very likely needs to be overridden with widget-specific default page

      // UI state
      active_tab: 0,
      expanded_general: [0],
      expanded_styles: [0],
      left_pane_collapsed: false,
      floating_control_visible: true,
      use_actual_size: 0,

      // integration state
      available_lists: [],
      customer_auto_enrich_feature_available: false,
      tag_new_form_customers_available: false,
      integration_tags: [],

      // edit status
      edit_status: null,
      validation_warnings: [],
      widget_message_handler: null,
      endpoint_validation_error: null,

      // legal patterns
      default_legal_patterns: {
        double_opt_in_pattern: '',
        after_double_opt_in_message: '',
        email_agreement_implied: '',
        email_agreement_checkbox: '',
        phone_agreement_implied: '',
        phone_agreement_checkbox: '',
      },
    };
  },

  computed: {
    /**
     * Whether the form has a published version
     */
    is_published() {
      return this.edit_status?.has_published_version;
    },

    /**
     * Whether there are unpublished draft changes
     */
    has_draft_changes() {
      if (!this.edit_status) return false;
      return this.edit_status.has_unpublished_changes;
    },

    /**
     * Whether the form can be unarchived
     */
    can_unarchive() {
      return this.form?.is_archived;
    },

    /**
     * Integration tags with a "No Tag" option prepended
     */
    integration_tags_with_none() {
      const no_tag_option = {
        tag_id: null,
        tag_label: 'No Tag',
      };
      return [no_tag_option, ...this.integration_tags];
    },

    /**
     * Preview dimensions based on preview mode
     */
    preview_dimensions() {
      const dimensions = {
        mobile: { width: 410, height: 844 },
        tablet: { width: 1024, height: 1366 },
        'tablet-landscape': { width: 1366, height: 1024 },
        desktop: { width: 1920, height: 1080 },
      };
      return dimensions[this.preview_mode] || dimensions['tablet-landscape'];
    },
  },

  mounted() {
    this.initializeWidget();
  },

  beforeUnmount() {
    this.cleanupWidget();
  },

  beforeRouteLeave(to, from, next) {
    const page_exceptions = this.getRouteLeaveExceptions();
    if (page_exceptions.includes(to.name)) {
      next();
      return;
    }

    if (this.$refs.widgetLayout) {
      this.$refs.widgetLayout.confirmLeaveWithUnpublishedChanges(next);
    } else {
      next();
    }
  },

  methods: {
    // ============================================================
    // REQUIRED METHODS - Must be overridden by consuming components
    // ============================================================

    /**
     * Get the endpoint for loading form and edit status
     * @returns {string} API endpoint
     */
    getLoadEndpoint() {
      throw new Error('getLoadEndpoint() must be implemented by the component');
    },

    /**
     * Get parameters for load request
     * @returns {Object} Parameters object
     */
    getLoadParams() {
      throw new Error('getLoadParams() must be implemented by the component');
    },

    /**
     * Get the endpoint for saving form data
     * @returns {string} API endpoint
     */
    getSaveEndpoint() {
      throw new Error('getSaveEndpoint() must be implemented by the component');
    },

    /**
     * Get the endpoint for publishing
     * @returns {string} API endpoint
     */
    getPublishEndpoint() {
      throw new Error('getPublishEndpoint() must be implemented by the component');
    },

    /**
     * Get parameters for publish request (beyond the specimen)
     * @returns {Object} Parameters object
     */
    getPublishParams() {
      throw new Error('getPublishParams() must be implemented by the component');
    },

    /**
     * Get the endpoint for discarding changes
     * @returns {string} API endpoint
     */
    getDiscardChangesEndpoint() {
      throw new Error('getDiscardChangesEndpoint() must be implemented by the component');
    },

    /**
     * Get parameters for discard changes request
     * @returns {Object} Parameters object
     */
    getDiscardParams() {
      throw new Error('getDiscardParams() must be implemented by the component');
    },

    /**
     * Get the endpoint for archiving/unarchiving
     * @returns {string} API endpoint
     */
    getArchiveEndpoint() {
      throw new Error('getArchiveEndpoint() must be implemented by the component');
    },

    /**
     * Get parameters for archive request
     * @returns {Object} Parameters object
     */
    getArchiveParams() {
      throw new Error('getArchiveParams() must be implemented by the component');
    },

    /**
     * Compose the preview parameters to send to the widget
     * @returns {Object} Preview parameters
     */
    composePreviewParams() {
      throw new Error('composePreviewParams() must be implemented by the component');
    },

    /**
     * Send settings to the widget iframe
     * @param {Object} settings - The settings to send
     */
    sendToWidget(settings) {
      throw new Error('sendToWidget() must be implemented by the component');
    },

    // ============================================================
    // OPTIONAL OVERRIDE METHODS - Can be overridden for custom behavior
    // ============================================================

    /**
     * Called after widget initialization is complete
     * Override to perform additional actions after initialization
     */
    onWidgetInitialized() {
      // override in component if needed
    },

    /**
     * Called after form data is loaded
     * Override to perform additional setup with loaded data
     * @param {Object} data - The loaded edit status data
     */
    onFormLoaded(data) {
      // override in component if needed
    },

    /**
     * Called after successful save
     * Override to perform additional actions after save
     * @param {Object} data - The saved form data
     */
    onSaveSuccess(data) {
      // override in component if needed
    },

    /**
     * Called after successful publish
     * Override to perform additional actions after publish
     */
    onPublishSuccess() {
      // override in component if needed
    },

    /**
     * Called after widget cleanup
     * Override to perform additional actions after cleanup
     */
    onWidgetCleanup() {
      // override in component if needed
    },

    /**
     * Get custom message handlers for widget messaging
     * Override if the widget required message handling beyond the default
     * Default handles PAGE_CHANGED messages, keeping current_page in sync
     * @returns {Object} Message handlers object
     */
    getCustomMessageHandlers() {
      return {};
    },

    /**
     * Get route names that should bypass the unpublished changes warning
     * Override to specify widget-specific route names
     * @returns {Array<string>} Array of route names
     */
    getRouteLeaveExceptions() {
      return [];
    },

    /**
     * Get the form label for display in dialogs
     * Override to customize form label retrieval
     * @returns {string} Form label
     */
    getFormLabel() {
      return this.form?.internal_label || 'Unknown'; 
    },

    // ============================================================
    // CORE FUNCTIONALITY - Shared implementation
    // ============================================================

    /**
     * Initialize the widget editor
     */
    initializeWidget() {
      this.loadFormAndEditStatus();
      this.loadAvailableLists();
      this.loadIntegrationFeatures();
      this.setupWidgetMessageListener();

      if (this.onWidgetInitialized) {
        this.onWidgetInitialized();
      }
    },

    /**
     * Cleanup when component is destroyed
     */
    cleanupWidget() {
      this.cleanupWidgetMessageListener();

      if (this.onWidgetCleanup) {
        this.onWidgetCleanup();
      }
    },

    // ============================================================
    // MESSAGE HANDLING
    // ============================================================

    /**
     * Setup message listener for widget communication
     */
    setupWidgetMessageListener() {
      const customHandlers = this.getCustomMessageHandlers();

      this.widget_message_handler = createParentMessageHandler({
        [WIDGET_TO_PARENT.PAGE_CHANGED]: (data) => {
          if (data?.page) {
            this.current_page = data.page;
          }
        },
        ...customHandlers,
      });

      window.addEventListener('message', this.widget_message_handler);
    },

    /**
     * Cleanup message listener
     */
    cleanupWidgetMessageListener() {
      if (this.widget_message_handler) {
        window.removeEventListener('message', this.widget_message_handler);
        this.widget_message_handler = null;
      }
    },

    // ============================================================
    // LOADING
    // ============================================================

    /**
     * Load form and edit status from server
     */
    loadFormAndEditStatus() {
      this.suppress_autosave = true;

      this.$rc.rest({
        spinner: true,
        endpoint: this.getLoadEndpoint(),
        params: this.getLoadParams(),
        on_success: data => {
          this.edit_status = { ...data };
          this.form = { ...data.draft_form };

          if (this.onFormLoaded) {
            this.onFormLoaded(data);
          }

          this.validateForm();

          this.$nextTick(() => {
            this.suppress_autosave = false;
          });
        },
        on_error: () => {
          this.$rc.snackbar('Unable to load form, please try refreshing the page.', 1500);
          this.suppress_autosave = false;
        },
      });
    },

    // ============================================================
    // SAVING
    // ============================================================

    /**
     * Handle form changes with debounced auto-save
     */
    onChange() {
      if (this.suppress_autosave) {
        return;
      }

      this.validateForm();

      if (this.save_debounce_handle) {
        clearTimeout(this.save_debounce_handle);
      }

      this.save_debounce_handle = setTimeout(() => {
        if (this.form) {
          this.saveForm();
        }
        this.preview_nonce = Date.now();

        this.$nextTick(() => {
          this.sendFormDataToWidget();
        });
      }, 750);
    },

    /**
     * Save form data to server
     */
    saveForm() {
      if (this.saving) return;

      this.saving = true;
      this.is_preview_loading = true;

      this.$rc.rest({
        spinner: false,
        endpoint: this.getSaveEndpoint(),
        params: this.form,
        on_success: data => {
          if (this.save_debounce_handle) {
            clearTimeout(this.save_debounce_handle);
            this.save_debounce_handle = null;
          }

          this.suppress_autosave = true;
          if (this.form && data) {
            Object.keys(data).forEach(k => {
              this.$set(this.form, k, data[k]);
            });
          }

          if (this.onSaveSuccess) {
            this.onSaveSuccess(data);
          }

          this.$nextTick(() => {
            this.suppress_autosave = false;
            this.preview_nonce = (this.preview_nonce || 0) + 1;
            this.saving = false;
            this.loadFormAndEditStatus();
          });
        },
        on_error: () => {
          this.$rc.snackbar('Unable to save form, please try again.', 1500);
          this.saving = false;
          this.is_preview_loading = false;

          if (this.save_debounce_handle) {
            clearTimeout(this.save_debounce_handle);
            this.save_debounce_handle = null;
          }
        },
      });
    },

    // ============================================================
    // PREVIEW
    // ============================================================

    /**
     * Handle preview iframe load event
     */
    onPreviewLoad() {
      this.is_preview_loading = false;

      this.$nextTick(() => {
        this.sendFormDataToWidget();
      });
    },

    /**
     * Send form data to widget iframe
     */
    sendFormDataToWidget() {
      if (!this.$refs['preview-iframe'] || !this.form) {
        return;
      }

      const settings = this.composePreviewParams();
      this.sendToWidget(settings);
    },

    /**
     * Handle dismissing floating control
     */
    onDismissFloatingControl() {
      this.floating_control_visible = false;
    },

    // ============================================================
    // PUBLISHING
    // ============================================================

    /**
     * Handle publish action
     * @param {Object} callbacks - Callbacks object with onSuccess and onError
     */
    handlePublish(callbacks) {
      if (!this.form) {
        callbacks.onError('Form must be saved before publishing');
        return;
      }

      const publish_params = {
        ...this.getPublishParams(),
      };

      if (this.$refs['publish-agreement']) {
        publish_params.specimen = this.$refs['publish-agreement'].getSpecimen();
      }

      this.$rc.rest({
        spinner: false,
        endpoint: this.getPublishEndpoint(),
        params: publish_params,
        on_success: () => {
          this.loadFormAndEditStatus();
          callbacks.onSuccess();

          if (this.onPublishSuccess) {
            this.onPublishSuccess();
          }
        },
        on_error: error => {
          callbacks.onError(error.message || 'Failed to publish. Please try again.');
        },
      });
    },

    /**
     * Handle discard changes action
     * @param {Object} callbacks - Callbacks object with onSuccess and onError
     */
    handleDiscardChanges(callbacks) {
      if (!this.form) {
        callbacks.onError?.();
        return;
      }

      this.$rc.rest({
        spinner: false,
        endpoint: this.getDiscardChangesEndpoint(),
        params: this.getDiscardParams(),
        on_success: draft_form => {
          this.form = draft_form;
          this.onChange();

          callbacks.onSuccess?.();
          this.$rc.snackbar('Changes discarded successfully!');
        },
        on_error: () => {
          callbacks.onError?.();
          this.$rc.snackbar('Failed to discard changes. Please try again.');
        },
      });
    },

    // ============================================================
    // ARCHIVING
    // ============================================================

    /**
     * Archive form
     * @param {Object} callbacks - Callbacks object with onSuccess and onError
     */
    archiveForm(callbacks) {
      if (!this.form) {
        callbacks.onError?.();
        return;
      }

      this.$rc.rest({
        spinner: false,
        endpoint: this.getArchiveEndpoint(),
        params: {
          ...this.getArchiveParams(),
          archived: true,
        },
        on_success: () => {
          callbacks.onSuccess?.();
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        },
        on_error: error => {
          callbacks.onError?.();
          this.$rc.snackbar(
            error.message || 'Failed to archive. Please try again.',
            3000,
            'error'
          );
        },
      });
    },

    /**
     * Show unarchive confirmation dialog
     */
    unarchiveForm() {
      const form_label = this.getFormLabel();

      this.$rc.confirm({
        title: `Unarchive Form`,
        body: `Are you sure you want to unarchive the draft version of the form: "${form_label}"? <br/><br/>This will not unarchive any published version of the form.`,
        btn_text_ok: 'Unarchive',
        btn_text_cancel: 'Cancel',
        on_ok: () => this.doUnarchive(),
      });
    },

    /**
     * Execute unarchive action
     */
    doUnarchive() {
      if (!this.form) return;

      // some widgets may have separate archive/unarchive endpoints
      const endpoint = this.getUnarchiveEndpoint ? this.getUnarchiveEndpoint() : this.getArchiveEndpoint();
      const params = this.getUnarchiveEndpoint ? this.getUnarchiveParams?.() || this.getArchiveParams() : {
        ...this.getArchiveParams(),
        archived: false,
      };

      this.$rc.rest({
        spinner: true,
        endpoint: endpoint,
        params: params,
        on_success: () => {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        },
        on_error: error => {
          this.$rc.snackbar(
            error.message || 'Failed to unarchive. Please try again.',
            3000,
            'error'
          );
        },
      });
    },

    // ============================================================
    // INTEGRATION FEATURES
    // ============================================================

    /**
     * Load available lists for autocomplete
     */
    loadAvailableLists() {
      this.$rc.rest({
        spinner: false,
        endpoint: 'LIST: /api/v1/autocomplete/lists',
        params: {},
        on_success: data => {
          this.available_lists = data.results.map(item => ({
            text: item.text,
            value: item.value,
          }));
        },
        on_error: () => {
          this.$rc.snackbar('Unable to load available lists', 1500);
        },
      });
    },

    /**
     * Load integration features availability
     */
    loadIntegrationFeatures() {
      this.checkFeatureAvailability('customers-auto-enrich-from-redchirp', is_supported => {
        this.customer_auto_enrich_feature_available = is_supported;
      });

      this.checkFeatureAvailability('tag-new-lightbox-customers-from-redchirp', is_supported => {
        this.tag_new_form_customers_available = is_supported;

        if (is_supported) {
          this.loadIntegrationTags('COMMERCE7');
        } else {
          this.integration_tags = [];
        }
      });
    },

    /**
     * Check if a feature is available
     * @param {string} feature_identifier - The feature identifier
     * @param {Function} on_success - Callback with availability result
     */
    checkFeatureAvailability(feature_identifier, on_success) {
      this.$rc.hasEnabledIntegrationFeature(
        feature_identifier,
        on_success,
        () => on_success(false)
      );
    },

    /**
     * Load integration tags
     * @param {string} integration_type - The integration type (e.g., 'COMMERCE7')
     */
    loadIntegrationTags(integration_type) {
      this.$rc.rest({
        spinner: false,
        endpoint: `GET: /api/v1/integrations/crm/tags/${this.$route.params.organization_id}/${integration_type}`,
        on_success: data => {
          this.integration_tags = data.tags || [];
        },
        on_error: () => {
          this.integration_tags = [];
        },
      });
    },

    /**
     * Handle new customer tag change
     * @param {string} tag_id - The selected tag ID
     */
    onNewCustomerTagChange(tag_id) {
      if (!this.form) return;

      if (tag_id && tag_id !== null) {
        this.form.new_customer_tag_integration_type = 'COMMERCE7';
      } else {
        this.form.new_customer_tag_integration_type = null;
      }
      this.onChange();
    },

    // ============================================================
    // VALIDATION
    // ============================================================

    /**
     * Fix a validation warning by navigating to the location
     * @param {Object} warning - The validation warning object
     */
    fixWarning(warning) {
      this.mixinGuideToFix(warning, {
        active_tab_model: 'active_tab',
        panel_model: warning.location.tab === 0 ? 'expanded_general' : 'expanded_styles',
      });
    },

    /**
     * Validate the form
     * Override this method in components to implement specific validation
     */
    validateForm() {
      this.validation_warnings = [];
      // override in component
    },

    /**
     * Handle endpoint validation from m-edit-select-organization-owned-endpoint
     * Allows for more nuanced validation i.e. not just checking if number is set, but verifying that selected number is not unavailable
     * This scenario could happen if a number is selected but is later made unavailable
     * @param {Object} payload - Validation payload with isValid and message
     */
    handleEndpointValidation(payload) {
      if (!payload.isValid) {
        this.endpoint_validation_error = payload.message;
      } else {
        this.endpoint_validation_error = null;
      }
      this.validateForm();
    },
  },
};
